import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { 
  XMarkIcon, 
  ExclamationTriangleIcon,
  CheckIcon,
  CurrencyDollarIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const UsageBasedPricingModal = ({ isOpen, onClose, onSuccess }) => {
  const [settings, setSettings] = useState({
    enabled: true,
    capType: 'capped',
    overageCapAmount: 20,
    agreementAccepted: false
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showLimitlessWarning, setShowLimitlessWarning] = useState(false);

  const handleCapTypeChange = (capType) => {
    setSettings(prev => ({ ...prev, capType }));
    if (capType === 'limitless') {
      setShowLimitlessWarning(true);
    } else {
      setShowLimitlessWarning(false);
      setSettings(prev => ({ ...prev, agreementAccepted: false }));
    }
  };

  const handleOverageCapChange = (value) => {
    const numValue = Math.max(20, parseInt(value) || 20);
    setSettings(prev => ({ ...prev, overageCapAmount: numValue }));
  };

  const handleSave = async () => {
    if (settings.capType === 'limitless' && !settings.agreementAccepted) {
      return; // Don't save if limitless selected but agreement not accepted
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/team-billing/enable-usage-based-pricing`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      onSuccess(settings);
      onClose();
    } catch (error) {
      console.error('Error enabling usage-based pricing:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const canSave = settings.capType === 'capped' || 
                 (settings.capType === 'limitless' && settings.agreementAccepted);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div 
        className="bg-white dark:bg-[#111111] rounded-2xl shadow-xl max-w-md w-full border border-[#e0e0e0] dark:border-[#282828]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#e0e0e0] dark:border-[#282828]">
          <div className="flex items-center space-x-3">
            <div className="bg-[#f0f0f0] dark:bg-[#1a1a1a] p-2 rounded-lg">
              <CurrencyDollarIcon className="w-6 h-6 text-[#525252] dark:text-[#999999]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Enable Usage-Based Pricing
              </h3>
              <p className="text-sm text-[#737373] dark:text-[#999999]">
                Configure overage billing settings
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[#737373] hover:text-[#525252] dark:hover:text-[#cccccc] transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 gap-3">
            {/* Capped Usage Option */}
            <div 
              className={`p-4 border rounded-xl cursor-pointer transition-all ${
                settings.capType === 'capped'
                  ? 'border-[#525252] dark:border-[#737373] bg-[#f5f5f5] dark:bg-[#1a1a1a]'
                  : 'border-[#e0e0e0] dark:border-[#282828] hover:border-[#b1b1b1] dark:hover:border-[#3c3c3c]'
              }`}
              onClick={() => handleCapTypeChange('capped')}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">Capped Usage</h4>
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  settings.capType === 'capped'
                    ? 'bg-black dark:bg-white border-black dark:border-white'
                    : 'border-[#d1d1d1] dark:border-[#282828]'
                }`}>
                  {settings.capType === 'capped' && (
                    <CheckIcon className="w-2.5 h-2.5 stroke-[3px] text-white dark:text-black" />
                  )}
                </div>
              </div>
              
              {settings.capType === 'capped' && (
                <div className="flex items-center space-x-2 mt-3">
                  <span className="text-sm text-[#737373] dark:text-[#999999]">Max overage:</span>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-900 dark:text-white mr-1">$</span>
                    <input
                      type="number"
                      min="20"
                      value={settings.overageCapAmount}
                      onChange={(e) => handleOverageCapChange(e.target.value)}
                      className="w-16 px-2 py-1 text-sm border border-[#d1d1d1] dark:border-[#282828] rounded-md bg-white dark:bg-[#111111] text-[#181818] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#525252] dark:focus:ring-[#999999]"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Limitless Usage Option */}
            <div 
              className={`p-4 border rounded-xl cursor-pointer transition-all ${
                settings.capType === 'limitless'
                  ? 'border-[#525252] dark:border-[#737373] bg-[#f5f5f5] dark:bg-[#1a1a1a]'
                  : 'border-[#e0e0e0] dark:border-[#282828] hover:border-[#b1b1b1] dark:hover:border-[#3c3c3c]'
              }`}
              onClick={() => handleCapTypeChange('limitless')}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Limitless Usage</h4>
                  
                </div>
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  settings.capType === 'limitless'
                    ? 'bg-black dark:bg-white border-black dark:border-white'
                    : 'border-[#d1d1d1] dark:border-[#282828]'
                }`}>
                  {settings.capType === 'limitless' && (
                    <CheckIcon className="w-2.5 h-2.5 stroke-[3px] text-white dark:text-black" />
                  )}
                </div>
              </div>
              <p className="text-xs text-[#737373] dark:text-[#999999]">No overage limits</p>
            </div>
          </div>

          {/* Limitless Warning */}
          {showLimitlessWarning && (
            <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-xl border border-amber-200 dark:border-amber-800">
              <div className="flex items-start space-x-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-amber-800 dark:text-amber-200 font-medium mb-2">
                    Unlimited Overage Charges
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
                    This will allow unlimited overage usage without any spending cap. You could incur significant unexpected charges.
                  </p>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setSettings(prev => ({ ...prev, agreementAccepted: !prev.agreementAccepted }))}
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                        settings.agreementAccepted
                          ? 'bg-black border-black dark:bg-white dark:border-white'
                          : 'bg-transparent border-[#737373] dark:border-[#999999] hover:border-[#525252] dark:hover:border-[#cccccc]'
                      }`}
                    >
                      {settings.agreementAccepted && (
                        <CheckIcon className={`w-3 h-3 stroke-[4px] ${
                          settings.agreementAccepted ? 'text-white dark:text-black' : ''
                        }`} />
                      )}
                    </button>
                    <span className="text-xs text-amber-800 dark:text-amber-200 cursor-pointer" onClick={() => setSettings(prev => ({ ...prev, agreementAccepted: !prev.agreementAccepted }))}>
                      I understand and accept the unlimited billing risk
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Current Settings Summary */}
          <div className="bg-[#f5f5f5] dark:bg-[#1a1a1a] p-3 rounded-lg">
            <div className="text-sm text-[#525252] dark:text-[#cccccc]">
              <span className="font-medium">Configuration:</span>{' '}
              {settings.capType === 'capped' 
                ? `Capped at $${settings.overageCapAmount} overage` 
                : 'Unlimited overage usage'
              }
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="text-sm flex space-x-3 p-6 border-t border-[#e0e0e0] dark:border-[#282828]">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-[#d1d1d1] dark:border-[#282828] text-[#404040] dark:text-[#cccccc] rounded-lg hover:bg-[#f5f5f5] dark:hover:bg-[#1a1a1a] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave || isSaving}
            className="flex-1 bg-black dark:bg-white text-white dark:text-black px-4 py-2.5 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {isSaving ? 'Enabling...' : 'Enable'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UsageBasedPricingModal; 